import React from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { StatCard } from "@outpave/ds";
import { formatCashFull } from "utils/formatCash";
import { PaymentLink } from "hooks/usePaymentLinkData";

interface PaymentLinkStatTilesProps {
  paymentLink: PaymentLink;
}

const TILE_SX = { height: "100%" };

const PaymentLinkStatTiles: React.FC<PaymentLinkStatTilesProps> = ({
  paymentLink,
}) => {
  const { t } = useTranslation();

  // Views are deduplicated per tab.
  const sessions = paymentLink.stats?.views || 0;
  const started = paymentLink.stats?.checkoutsStarted || 0;
  const paid = paymentLink.stats?.sales || 0;
  const refunded = paymentLink.stats?.refunded || 0;
  const fees = paymentLink.stats?.platformFees || 0;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gridAutoRows: "1fr",
        gap: "var(--space-3)",
      }}
    >
      <StatCard sx={TILE_SX} title={t("Checkout Sessions")} value={sessions} />
      {/* Retries can make checkouts exceed deduplicated views, so omit this conversion rate. */}
      <StatCard sx={TILE_SX} title={t("Checkouts started")} value={started} />
      <StatCard
        sx={TILE_SX}
        title={t("Paid")}
        value={paid}
        caption={shareOfSessions(paid, sessions, t)}
      />
      <StatCard
        sx={TILE_SX}
        title={t("Total")}
        value={formatCashFull(
          paymentLink.stats?.netCollected || 0,
          paymentLink.currency,
        )}
        caption={whatCameOff(refunded, fees, paymentLink.currency, t)}
      />
    </Box>
  );
};

function whatCameOff(
  refunded: number,
  fees: number,
  currency: string,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  if (refunded && fees) {
    return t("AfterRefundsAndFees", {
      refunded: formatCashFull(refunded, currency),
      fees: formatCashFull(fees, currency),
    });
  }
  if (fees) {
    return t("AfterFees", { amount: formatCashFull(fees, currency) });
  }
  if (refunded) {
    return t("AfterRefunds", { amount: formatCashFull(refunded, currency) });
  }

  return undefined;
}

function shareOfSessions(
  count: number,
  sessions: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): string | undefined {
  if (!sessions || !count) return undefined;

  return t("ShareOfSessions", {
    percent: ((count / sessions) * 100).toFixed(1),
  });
}

export default PaymentLinkStatTiles;
